import "./styles/MenuBar.scss";
import React, {useEffect, useRef, useState} from "react";
import MenuBarItem from "./MenuBarItem";
import {Menu, MenuItem} from "navigation-common/types/navigation-types";
import {navigationService} from "../api/NavigationService";
import ChevronLeftLargeIcon from '@atlaskit/icon/glyph/chevron-left-large'
import ChevronRightLargeIcon from '@atlaskit/icon/glyph/chevron-right-large'
import {useDebounce, useMutationObserver} from "rooks";
import {frontendService} from "../common/FrontendUtils";
import {confluenceService} from "../api/ConfluenceService";
import EditFilledIcon from '@atlaskit/icon/glyph/edit-filled'
import {PAGE_ADMIN_SINGLE_MENU_CONFIG, PAGE_GENERAL_SINGLE_MENU_CONFIG, PARAM_APP_MENU_CONFIG_MENU_KEY} from "navigation-common/types/pages-constants";
import classNames from "classnames";
import {LicenseCheckInfo} from "./LicenceCheckInfo";
import {useAsync, useAsyncFn, useEffectOnce} from "react-use";
import {menuCache} from "./MenuCache";
import {Fade} from "transitions-kit";

import {ReviewBanner} from "./banner/ReviewBanner";
import {menuActivityController} from "./banner/MenuActivityController";
import {IconButton} from "@atlaskit/button/new";
import CrossIcon from '@atlaskit/icon/glyph/cross';

interface MenuBarProps {
    menu: Menu | undefined,
    changeToggle: boolean,
    preview?: boolean
}

const SCREEN_SIZE_THRESHOLD = 760;

function MenuBar(props: MenuBarProps) {
    const [active, setActive] = React.useState("");
    const [menu, setMenu] = React.useState(props.menu);
    const [scrollLeft, setScrollLeft] = React.useState<number>(0);
    const [limit, setLimit] = React.useState<number>(-1);
    const [isCurrentUserEditor, setIsCurrentUserEditor] = React.useState<boolean>(false);
    const [isHidden, setIsHidden] = React.useState(false);
    const ulWrapper = useRef<HTMLUListElement>(null);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < SCREEN_SIZE_THRESHOLD);
    const [enableReviewBanner, setEnableReviewBanner] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsSmallScreen(window.innerWidth <= SCREEN_SIZE_THRESHOLD);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const changeLimit = useDebounce(() => {
        if (ulWrapper.current) {
            setLimit(ulWrapper.current.scrollWidth - ulWrapper.current.clientWidth);
        }
    }, 100);
    useMutationObserver(ulWrapper, changeLimit);

    const activatedMenuItem: MenuItem | undefined = menu?.menuItems.filter((menuItem: MenuItem) => menuItem.key === active).pop();

    const [, loadMenu] = useAsyncFn(async () => {
        try {
            await initMenu();
        } catch (e) {
            console.warn("Could not initialize Menu", e);
            setIsHidden(true);
        }
    });

    useEffectOnce(() => {
        AP.navigator.getLocation(async (location: any) => {
            if (location?.href && location?.target && confluenceService.isForbiddenSite(location.href, location.target)) {
                setIsHidden(true);
                return;
            }
            await loadMenu();
        });

        window.addEventListener('resize', changeLimit);
        return () => {
            window.removeEventListener('resize', changeLimit);
        }
    });

    useEffect(() => {
        if (menu) {
            applyMenuColors(menu);
            frontendService.preloadImages(menu);
        }
    }, [menu, props.changeToggle]);

    const initMenu = async () => {
        navigationService.isCurrentUserEditor().then((isEditor) => {
            setIsCurrentUserEditor(isEditor);
        });

        if (props.menu) {
            setMenu(props.menu);
            return Promise.resolve();
        }

        const currentSpaceKey = await confluenceService.getCurrentSpace();
        const loadedMenu = await menuCache.get(currentSpaceKey);
        setMenu(loadedMenu);
        setIsHidden(!(loadedMenu?.menuItems?.length));
    };

    const applyMenuColors = (menu: Menu) => {
        document.documentElement.style.setProperty("--menu-font-color", frontendService.isBright(menu.primaryColor) ? "rgb(var(--ak-color-n800-rgb))" : "rgb(var(--ak-color-n0-rgb))");
        document.documentElement.style.setProperty("--menu-active-color", frontendService.isBright(menu.primaryColor) ? "rgba(var(--ak-color-n800-rgb), 0.6)" : "rgba(var(--ak-color-n0-rgb), 0.6)");
        document.documentElement.style.setProperty("--menu-hover-color", frontendService.isBright(menu.primaryColor) ? "rgba(var(--ak-color-n800-rgb), 0.15)" : "rgba(var(--ak-color-n0-rgb), 0.15)");
        document.documentElement.style.setProperty("--menu-font-color-dark", frontendService.isBright(menu.primaryColorDarkTheme) ? "rgb(var(--ak-color-n800-rgb))" : "rgb(var(--ak-color-n0-rgb))");
        document.documentElement.style.setProperty("--menu-active-color-dark", frontendService.isBright(menu.primaryColorDarkTheme) ? "rgba(var(--ak-color-n800-rgb), 0.6)" : "rgba(var(--ak-color-n0-rgb), 0.6)");
        document.documentElement.style.setProperty("--menu-hover-color-dark", frontendService.isBright(menu.primaryColorDarkTheme) ? "rgba(var(--ak-color-n800-rgb), 0.15)" : "rgba(var(--ak-color-n0-rgb), 0.15)");

        document.documentElement.style.setProperty("--menu-primary-color", frontendService.hexToRgbColor(menu.primaryColor));
        document.documentElement.style.setProperty("--menu-primary-colordark", frontendService.hexToRgbColor(menu.primaryColorDarkTheme ?? menu.primaryColor));

        document.documentElement.style.setProperty("--submenu-font-color", frontendService.isBright(menu.subMenuBgColor) ? "rgb(var(--ak-color-n800-rgb))" : "rgb(var(--ak-color-n0-rgb))");
        document.documentElement.style.setProperty("--submenu-hover-color", frontendService.isBright(menu.subMenuBgColor) ? "rgba(var(--ak-color-n800-rgb), 0.15)" : "rgba(var(--ak-color-n0-rgb), 0.15)");
        document.documentElement.style.setProperty("--submenu-font-color-dark", frontendService.isBright(menu.subMenuBgColorDarkTheme) ? "rgb(var(--ak-color-n800-rgb))" : "rgb(var(--ak-color-n0-rgb))");
        document.documentElement.style.setProperty("--submenu-hover-color-dark", frontendService.isBright(menu.subMenuBgColorDarkTheme) ? "rgba(var(--ak-color-n800-rgb), 0.15)" : "rgba(var(--ak-color-n0-rgb), 0.15)");

        document.documentElement.style.setProperty("--submenu-closebtn-color", frontendService.isBright(menu.subMenuBgColor) ? "var(--ak-color-n800-rgb)" : "var(--ak-color-n0-rgb)");
        document.documentElement.style.setProperty("--submenu-closebtn-color-dark", frontendService.isBright(menu.subMenuBgColorDarkTheme) ? "var(--ak-color-n800-rgb)" : "var(--ak-color-n0-rgb)");

        document.documentElement.style.setProperty("--submenu-bg-color", frontendService.hexToRgbColor(menu.subMenuBgColor));
        document.documentElement.style.setProperty("--submenu-bg-colordark", frontendService.hexToRgbColor(menu.subMenuBgColorDarkTheme ?? menu.subMenuBgColor));
    }

    const scrollMenu = (amount: number) => {
        if (ulWrapper.current) {
            ulWrapper.current.scrollLeft = scrollLeft + amount;
        }
        setScrollLeft(scrollLeft + amount);
    };

    const onClickedMenuLvl1 = async (menuItemKey: string) => {
        setActive(menuItemKey);
        if (menuItemKey === "") {
            setEnableReviewBanner(false);
            return;
        }
        const clickCount = await menuActivityController.incrementClickedMenuActivity();
        if (clickCount > 1) {
            setEnableReviewBanner(true);
        }
    }

    return (
        <div id={"atl-cloud-navigation"} className={classNames("ac-content", "desktop-view", {"hidden": isHidden, "preview": props.preview})}>
            {!props.preview && !isHidden && menu && menu.menuItems.length > 0 && <><LicenseCheckInfo/><ReviewBanner isEnabled={enableReviewBanner}/></>}
            <div className={"level-1-container"}>
                <div className={"level-1-user-content"}>
                    <ul className={"level-1"} ref={ulWrapper}>
                        {
                            menu?.menuItems.map((menuItem) => {
                                return <MenuBarItem menuItem={menuItem} preview={props.preview} onPress={onClickedMenuLvl1} activated={active === menuItem.key}
                                                    key={menuItem.key} isSmallScreen={isSmallScreen}/>
                            })
                        }
                    </ul>
                    {scrollLeft >= 10 &&
                        <button id="nav__scroll--left" className="nav__scroll nav__scroll--left hide" onClick={() => scrollMenu(-300)}>
                            <ChevronLeftLargeIcon label={"left"}/>
                        </button>
                    }
                    {scrollLeft < limit &&
                        <button id="nav__scroll--right" className="nav__scroll nav__scroll--right hide" onClick={() => scrollMenu(300)}>
                            <ChevronRightLargeIcon label={"right"}/>
                        </button>
                    }
                </div>
                {menu?.menuItems.length !== 0 && isCurrentUserEditor &&
                    <div className={"level-1-admin-content"}>
                        <EditButton isDisabled={props.preview ?? false} menuKey={menu?.key ?? ""}/>
                    </div>
                }
            </div>

            {activatedMenuItem?.subMenuItems && activatedMenuItem.subMenuItems.length > 0 &&
                <Fade in={activatedMenuItem.subMenuItems.length > 0} timeout={250}>
                    <div className={classNames("level-2-container", {"background-set": activatedMenuItem.imageUrl?.length})}>
                        <div className={"close-icon"}>
                            <IconButton icon={(iconProps) => (<CrossIcon {...iconProps} size="small" />)} label={"close"}
                                                                  onClick={() => setActive("")} spacing="compact"/>
                        </div>
                        <div className={"level-2-content-wrapper"}>
                            <ul className={classNames("level-2")}>
                                {activatedMenuItem.subMenuItems.map((menuItem: MenuItem) => {
                                    return <MenuBarItem menuItem={menuItem} preview={props.preview} onPress={setActive} activated={active === menuItem.key}
                                                        key={menuItem.key} isSmallScreen={isSmallScreen}/>
                                })}
                            </ul>
                            {activatedMenuItem?.imageUrl && activatedMenuItem?.imageUrl.length > 0 &&
                                <div className={"background-image"} style={{backgroundImage: `url(${activatedMenuItem.imageUrl})`}}></div>
                            }
                        </div>
                    </div>
                </Fade>
            }
        </div>
    );
}

const EditButton = ({isDisabled, menuKey}: { isDisabled: boolean, menuKey: string }) => {
    const [isCurrentUserAdmin, setIsCurrentUserAdmin] = React.useState<boolean>(false);

    useAsync(async () => {
        try {
            const isAdmin = await confluenceService.isCurrentUserAdmin();
            setIsCurrentUserAdmin(isAdmin);
        } catch (e) {
            console.warn("Could not determine if user is admin");
        }
    });

    const onEditButtonClicked = () => {
        if (isDisabled) {
            return;
        }
        AP.navigator.go("addonModule", {
            moduleKey: isCurrentUserAdmin ? PAGE_ADMIN_SINGLE_MENU_CONFIG.key : PAGE_GENERAL_SINGLE_MENU_CONFIG.key,
            customData: {
                [PARAM_APP_MENU_CONFIG_MENU_KEY]: menuKey,
            },
        })
    };

    return (
        <div className={"edit-button clickable"} onClick={onEditButtonClicked}>
            <EditFilledIcon primaryColor={"var(--menu-font-color)"} label={"configure menu"} size={"medium"}/>
        </div>
    );
}

export default MenuBar;
